<template>
  <Dialog
    :visible="internalVisible"
    modal
    :header="t('event.karaoke.directModalTitle')"
    :style="{ width: '450px' }"
    :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    class="karaoke-signup-modal"
    :pt="dialogPt"
    @update:visible="handleUpdateVisible"
  >
    <div class="p-fluid">
      <div
        v-if="isAlreadyQueued"
        class="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
        data-testid="karaoke-already-queued-warning"
      >
        <p class="text-sm text-amber-800 dark:text-amber-200 m-0">
          <i class="pi pi-exclamation-triangle mr-1"></i>
          <strong>{{ t("event.karaoke.alreadyQueuedTitle") }}</strong>
          {{ t("event.karaoke.alreadyQueuedMessage") }}
        </p>
      </div>

      <!-- Song Info -->
      <div v-if="song" class="mb-4">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {{ t("event.karaoke.selectedSong") }}
        </label>
        <div class="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white m-0 mb-1">
            {{ song.title }}
          </h3>
          <p v-if="song.author" class="text-gray-600 dark:text-gray-400 text-sm m-0">
            by {{ song.author }}
          </p>
        </div>
      </div>

      <!-- Instructions -->
      <div class="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h4 class="text-base font-semibold text-blue-900 dark:text-blue-200 m-0 mb-2">
          {{ t("event.karaoke.toSingThisSong") }}
        </h4>
        <p class="text-blue-800 dark:text-blue-200 text-sm leading-relaxed m-0">
          {{ t("event.karaoke.findAdminInstructions") }}
        </p>
        <ul class="text-blue-800 dark:text-blue-200 text-sm mt-2 space-y-1 mb-0">
          <li><strong>{{ t("event.karaoke.songLabel") }}:</strong> {{ songTitle }}</li>
          <li><strong>{{ t("event.karaoke.nameLabel") }}:</strong> {{ t("event.karaoke.yourNamePlaceholder") }}</li>
        </ul>
        <p class="text-blue-800 dark:text-blue-200 text-sm mt-3">
          {{ t("event.karaoke.adminWillAddYou") }}
        </p>
      </div>

      <!-- Important Note -->
      <div class="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
        <p class="text-amber-800 dark:text-amber-200 text-xs leading-relaxed m-0">
          <i class="pi pi-info-circle mr-1"></i>
          {{ t("event.karaoke.physicalPresenceRequired") }}
        </p>
      </div>
    </div>

    <template #footer>
      <Button
        :label="t('event.karaoke.gotIt')"
        severity="primary"
        @click="closeModal"
        autofocus
      />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Dialog from "primevue/dialog";
import Button from "primevue/button";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  song: {
    type: Object,
    default: null
  },
  isAlreadyQueued: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:visible", "close"]);

const { t } = useI18n();

const dialogPt = {
  root: { class: "bg-white dark:bg-gray-900 mx-4 sm:mx-0" },
  header: { class: "bg-white dark:bg-gray-900 dark:text-white font-heading rounded-t-xl" },
  content: { class: "bg-white dark:bg-gray-900 dark:text-gray-200" },
  footer: { class: "bg-white dark:bg-gray-900 dark:border-gray-700" },
  closeButton: { class: "dark:hover:bg-gray-800 dark:text-gray-400 dark:hover:text-gray-200" }
};

// Local state for dialog visibility
const internalVisible = ref(props.visible);

watch(() => props.visible, (newVal) => {
  internalVisible.value = newVal;
});

// Computed
const songTitle = computed(() => {
  return props.song?.title || "Unknown Song";
});

// Methods
const handleUpdateVisible = (value) => {
  internalVisible.value = value;
  emit("update:visible", value);
};

const closeModal = () => {
  internalVisible.value = false;
  emit("update:visible", false);
  emit("close");
};
</script>

<style scoped>
.karaoke-signup-modal :deep(.p-dialog-header) {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.karaoke-signup-modal :deep(.p-dialog-content) {
  max-height: 70vh;
  overflow-y: auto;
}

.karaoke-signup-modal :deep(.p-dialog-footer) {
  display: flex;
  justify-content: center;
}
</style>
